import {
    hasNewActivityFailure,
    hasNewActivityWarning,
} from './attention-transition';
import { activityJobTypesSharePhase } from './identity';
import {
    hasConflictingActivityBuildingIds,
    mergeActivityJob,
    newestActivityTimestamp,
    toActivityJob,
} from './job';
import type { ActivityItem, ActivityJobUpdate } from './models';
import { activitySectionFor, isAttentionActivitySection } from './policy';
import {
    isActivityIntegrityQuarantined,
    MAX_STORED_JOBS_PER_ITEM,
} from './storage-limits';

export function updateExistingActivityItem(
    item: ActivityItem,
    update: ActivityJobUpdate,
    preserveLineageBuildingIdentity: boolean
): ActivityItem {
    const exactJobIndex = item.jobs.findIndex(
        (job) => job.jobId === update.jobId
    );
    const samePhaseJobIndex = item.jobs.findIndex((job) =>
        activityJobTypesSharePhase(job.jobType, update.jobType)
    );
    const replacesPhase =
        exactJobIndex === -1 &&
        samePhaseJobIndex !== -1 &&
        update.correlationId !== null;
    const jobIndex = replacesPhase ? samePhaseJobIndex : exactJobIndex;
    const existingJob = jobIndex === -1 ? null : item.jobs[jobIndex];
    const observedJobIds = appendObservedJobId(item, update.jobId);
    const updatedAtUtc = newestActivityTimestamp(
        item.updatedAtUtc,
        update.occurredAtUtc
    );
    const withObservedJobId = (): ActivityItem =>
        observedJobIds === item.observedJobIds
            ? item
            : { ...item, observedJobIds };
    const conflictingExactJobBuilding =
        exactJobIndex !== -1 &&
        existingJob?.buildingPublicId !== null &&
        update.buildingPublicId !== null &&
        existingJob?.buildingPublicId !== update.buildingPublicId;
    const conflictingExactExportIdentity =
        exactJobIndex !== -1 &&
        existingJob !== null &&
        ((existingJob.exportKind !== null &&
            existingJob.exportKind !== undefined &&
            update.exportKind !== null &&
            update.exportKind !== undefined &&
            existingJob.exportKind !== update.exportKind) ||
            (existingJob.packId !== null &&
                update.packId !== null &&
                existingJob.packId !== update.packId));

    if (conflictingExactJobBuilding || conflictingExactExportIdentity) {
        return {
            ...item,
            observedJobIds,
            availability: 'unavailable',
            identityQuarantined: true,
            seen: false,
        };
    }

    if (
        replacesPhase &&
        existingJob &&
        Date.parse(update.occurredAtUtc) <=
            Date.parse(existingJob.occurredAtUtc)
    ) {
        return withObservedJobId();
    }

    const identitySafeUpdate =
        preserveLineageBuildingIdentity && existingJob?.buildingPublicId
            ? { ...update, buildingPublicId: existingJob.buildingPublicId }
            : update;
    const nextJob = existingJob
        ? replacesPhase
            ? toActivityJob(identitySafeUpdate)
            : mergeActivityJob(existingJob, identitySafeUpdate)
        : toActivityJob(identitySafeUpdate);

    if (nextJob === existingJob) {
        return withObservedJobId();
    }

    const previousSection = activitySectionFor(item);
    const replacementConflictsWithExistingBuilding =
        replacesPhase &&
        hasConflictingActivityBuildingIds([...item.jobs, nextJob]);
    const observedJobs =
        jobIndex === -1 || replacementConflictsWithExistingBuilding
            ? [...item.jobs, nextJob]
            : item.jobs.map((job, index) =>
                  index === jobIndex ? nextJob : job
              );
    const overflowed = observedJobs.length > MAX_STORED_JOBS_PER_ITEM;
    const hasConflictingBuildings =
        hasConflictingActivityBuildingIds(observedJobs);
    const integrityQuarantined = isActivityIntegrityQuarantined(item);
    const next: ActivityItem = {
        ...item,
        jobs: observedJobs,
        observedJobIds,
        updatedAtUtc,
        actedOn: exactJobIndex !== -1 ? item.actedOn : false,
        availability:
            integrityQuarantined || overflowed || hasConflictingBuildings
                ? 'unavailable'
                : 'available',
        identityQuarantined:
            item.identityQuarantined === true || hasConflictingBuildings,
        seen:
            integrityQuarantined || overflowed || hasConflictingBuildings
                ? false
                : item.seen,
    };
    const nextSection = activitySectionFor(next);
    const hasNewAttention =
        hasNewActivityFailure(item, next) || hasNewActivityWarning(item, next);

    return isAttentionActivitySection(nextSection) &&
        (previousSection !== nextSection || hasNewAttention)
        ? { ...next, seen: false }
        : next;
}

function appendObservedJobId(
    item: ActivityItem,
    jobId: string
): readonly string[] {
    const observedJobIds =
        item.observedJobIds ?? item.jobs.map((job) => job.jobId);
    return observedJobIds.includes(jobId)
        ? observedJobIds
        : [...observedJobIds, jobId];
}
